/**
 * Client-side mirror of the AE-211 final-plus routing parser. The edge
 * function remains authoritative through `check_invite_email_domain`; this
 * only avoids submitting obviously invalid invite addresses.
 */
export interface EmailDistrictRouting {
  identity: string;
  simulatedKey: string;
  hostKey: string;
  hasPlus: boolean;
}

export function resolveEmailDistrictRouting(email: string | null | undefined): EmailDistrictRouting | null {
  const identity = email?.trim().toLowerCase();
  if (!identity || /\s/.test(identity)) return null;

  const atIndex = identity.indexOf("@");
  if (atIndex <= 0 || atIndex !== identity.lastIndexOf("@")) return null;

  const local = identity.slice(0, atIndex);
  const hostKey = identity.slice(atIndex + 1);
  if (!/^[^.@\s]+(?:\.[^.@\s]+)+$/.test(hostKey)) return null;

  const lastPlus = local.lastIndexOf("+");
  if (lastPlus === -1) {
    return { identity, simulatedKey: hostKey, hostKey, hasPlus: false };
  }

  const finalPlus = local.slice(lastPlus + 1);
  if (!finalPlus) return null;

  return {
    identity,
    simulatedKey: `${finalPlus}@${hostKey}`,
    hostKey,
    hasPlus: true,
  };
}

export function normalizeEmailDomain(email: string | null | undefined): string | null {
  return resolveEmailDistrictRouting(email)?.simulatedKey ?? null;
}

export function emailMatchesDistrictDomain(email: string, districtDomain: string | null): boolean {
  const targetDomain = districtDomain?.trim().toLowerCase();
  const routing = resolveEmailDistrictRouting(email);
  if (!targetDomain || !routing) return false;

  // A host-only district is a valid local preview for a plus address when its
  // simulated key is absent. The server performs the database-backed priority
  // check and rejects it if a simulated district is provisioned.
  return routing.simulatedKey === targetDomain || (routing.hasPlus && routing.hostKey === targetDomain);
}
